
import { Injectable, inject, signal, DOCUMENT } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import moment from 'moment';
import { DEFAULT_INTERFACE_LANGUAGE, isRtlLanguage, normalizeInterfaceLanguage } from './languages';
import { momentLocaleFor } from './moment-locales';
import { environment } from '../../../environments/environment';

const STORAGE_KEY = 'interface_language';
const RTL_THEME_ID = 'charla-rtl-theme';
const RTL_THEME_URL = `assets/css/theme.rtl.css?v=${environment.version}`;
// the global stylesheet emitted by the Angular build (styles.css, styles-HASH.css)
const LTR_THEME_SELECTOR = 'link[rel="stylesheet"][href*="styles"]';

@Injectable({ providedIn: 'root' })
export class I18nService {
	private translate = inject(TranslateService);
	private document = inject(DOCUMENT);
	private cache = new Map<string, string>();
	private cacheLang: string | null = null;

	/** Reactive current language code (null until the first language is loaded) */
	readonly currentLang = this.translate.currentLang;

	private readonly rtl = signal(false);

	/** Reactive: true while the interface language is written right to left */
	readonly isRtl = this.rtl.asReadonly();

	/**
	 * Picks the initial language: saved choice, then the browser language, then English.
	 * The user's profile language replaces it once the user is loaded.
	 */
	init(): Promise<void> {
		const initial =
			normalizeInterfaceLanguage(localStorage.getItem(STORAGE_KEY)) ??
			normalizeInterfaceLanguage(this.translate.getBrowserCultureLang() ?? this.translate.getBrowserLang()) ??
			DEFAULT_INTERFACE_LANGUAGE;

		return this.use(initial);
	}

	/** Current language code, falling back to the default one */
	get language(): string {
		return this.translate.getCurrentLang() ?? normalizeInterfaceLanguage(localStorage.getItem(STORAGE_KEY)) ?? DEFAULT_INTERFACE_LANGUAGE;
	}

	/** Switches the interface language. Unknown codes fall back to the default language. */
	async use(code: string | null | undefined): Promise<void> {
		const lang = normalizeInterfaceLanguage(code) ?? DEFAULT_INTERFACE_LANGUAGE;

		try {
			await firstValueFrom(this.translate.use(lang));
		} catch (e) {
			// the English defaults in the templates are still shown
			console.warn(`Could not load translations for ${lang}`, e);
		}

		localStorage.setItem(STORAGE_KEY, lang);
		this.document.documentElement.lang = lang;
		moment.locale(momentLocaleFor(lang));
		await this.applyDirection(isRtlLanguage(lang));
	}

	/**
	 * Sets dir="rtl|ltr" on <html> and swaps the global stylesheet for its right-to-left build.
	 * Component styles use CSS logical properties, so they follow `dir` on their own.
	 */
	private async applyDirection(rtl: boolean): Promise<void> {
		const root = this.document.documentElement;
		root.dir = rtl ? 'rtl' : 'ltr';
		// the Metronic theme mixins look for [direction="rtl"]
		root.setAttribute('direction', root.dir);
		this.rtl.set(rtl);

		const ltrThemes = Array.from(this.document.querySelectorAll<HTMLLinkElement>(LTR_THEME_SELECTOR)).filter(l => l.id !== RTL_THEME_ID);
		let rtlTheme = this.document.getElementById(RTL_THEME_ID) as HTMLLinkElement | null;

		if (rtl && !rtlTheme) {
			rtlTheme = this.document.createElement('link');
			rtlTheme.id = RTL_THEME_ID;
			rtlTheme.rel = 'stylesheet';
			rtlTheme.href = RTL_THEME_URL;
			const loaded = new Promise<void>(resolve => {
				rtlTheme!.onload = () => resolve();
				rtlTheme!.onerror = () => resolve();
			});
			this.document.head.appendChild(rtlTheme);
			await loaded;
		}

		if (rtlTheme) {
			rtlTheme.disabled = !rtl;
		}
		// only turn the LTR theme off when the RTL one is really there
		const rtlReady = rtl && !!rtlTheme && !!rtlTheme.sheet;
		ltrThemes.forEach(l => (l.disabled = rtlReady));
		if (rtl && !rtlReady) {
			console.warn('RTL stylesheet could not be loaded');
		}
	}

	/**
	 * Returns the translation for the key, or the English default when
	 * the key is missing from the current language file.
	 */
	t(key: string, defaultText: string, params?: Record<string, unknown>): string {
		// read the signal so templates and computed values update on language change
		const lang = this.currentLang();
		if (lang !== this.cacheLang) {
			this.cache.clear();
			this.cacheLang = lang;
		}

		// translations without parameters are the vast majority and are rendered on every change detection
		const cached = params ? undefined : this.cache.get(key);
		if (cached !== undefined) {
			return cached;
		}

		const translated = this.translate.instant(key, params);
		let result: string;
		if (typeof translated === 'string' && translated !== key) {
			result = translated;
		} else {
			result = params
				? defaultText.replace(/{{\s*(\w+)\s*}}/g, (m, name) => (name in params ? String(params[name]) : m))
				: defaultText;
		}

		if (!params) {
			this.cache.set(key, result);
		}
		return result;
	}
}
